import { useEffect, useState } from "react";
import { apiFetch, devProfileOverride } from "@shared/api-client";
import { setAnalyticsCompany } from "@/lib/analytics";
import {
	parseInquiryProfileConfig,
	type InquiryProfileConfig,
} from "./inquiry-profile-schema";

export type InquiryProfileLoadState =
	| { status: "loading"; retry: () => void }
	| { status: "error"; message: string; retry: () => void }
	| { status: "success"; profile: InquiryProfileConfig; retry: () => void };

type ProfileState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "success"; profile: InquiryProfileConfig };

export function useInquiryProfile(): InquiryProfileLoadState {
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<ProfileState>({ status: "loading" });
	const retry = (): void => setAttempt((value) => value + 1);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadProfile(): Promise<void> {
      try {
        // Dev-only: a stored profile route_slug loads that profile of the same
        // tenant instead of the default (the profile switcher). Bare-localhost
        // gated in devProfileOverride — production always hits the default.
        const devProfile = devProfileOverride();
        const loaded = await apiFetch<unknown>(
					devProfile
						? `/api/inquiry/profiles/${devProfile}`
						: "/api/inquiry/profile",
          { signal: controller.signal },
        );
				const profile = parseInquiryProfileConfig(loaded);
				if (!profile) {
					throw new Error("The enquiry form configuration is invalid.");
				}
				if (active) {
					setAnalyticsCompany(profile.company?.slug);
					setState({ status: "success", profile });
        }
			} catch (error) {
        if (!controller.signal.aborted && active) {
					setState({
						status: "error",
						message:
							error instanceof Error
								? error.message
								: "We couldn't load this enquiry form.",
					});
        }
      }
    }

		setState({ status: "loading" });
    void loadProfile();

    return () => {
      active = false;
      controller.abort();
    };
  }, [attempt]);

	return { ...state, retry } as InquiryProfileLoadState;
}
